import { LitElement, css, html } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import '@maersk-global/mds-components-core'
import '@maersk-global/mds-components-core/mc-popover'
import './activity-log-items'
import { VesselNotificationsModel } from '../../../../../src/models/vessel-notifications-model'
import {
    UnifiedNotification,
    VesselNotificationsEventNames,
} from '../../../../../src/models/types/vessel-notifications.types'
import { ActivityLogItem, createActivityLogItems } from './activity-schema';     

@customElement('activity-log-cloud')
export class ActivityLog extends LitElement {
    @state() notifications: UnifiedNotification[] = [];
    @state() searchQuery: string = '';
    @state() private activityLogItems: ActivityLogItem[] = [];

    constructor() {
        super();
        this.handleNotificationsUpdate = this.handleNotificationsUpdate.bind(this);
    }

     connectedCallback() {
        super.connectedCallback();
        this.notifications = VesselNotificationsModel?.notifications || [];
        this.activityLogItems = createActivityLogItems(this.notifications);
        VesselNotificationsModel?.addEventListener(
            VesselNotificationsEventNames.NotificationsUpdated,
            this.handleNotificationsUpdate,
        );
        VesselNotificationsModel!.addEventListener('update-activity-log', async (e) => {
            const evt = e as CustomEvent;
            const currentSchedules = evt?.detail?.schedules || [];
            await VesselNotificationsModel?.loadSyncNotifications();
            await VesselNotificationsModel?.checkBasicPlanAvailable(currentSchedules);
        });
    }

    disconnectedCallback() {
        VesselNotificationsModel?.removeEventListener(
            VesselNotificationsEventNames.NotificationsUpdated,
            this.handleNotificationsUpdate,
        );
        VesselNotificationsModel!.removeEventListener('update-activity-log', async () => {
            await VesselNotificationsModel?.loadSyncNotifications();
            await VesselNotificationsModel?.checkBasicPlanAvailable([]);
        });
    }

    static get styles() {
        return [
            css`
                .notifications {
                    max-height: 80vh;
                    overflow-y: auto;
                }

                .empty-state {
                    padding: 16px;
                    text-align: center;
                    color: #767676;
                }

                .search-container {
                    padding: 16px;
                }

                .popover-trigger {
                    display: flex;
                    align-items: center;
                    cursor: pointer;
                    gap: 5px;
                    font-family: MaerskText;
                    font-size: 14px;
                    line-height: 16px;
                    margin-right: 8px;
                }

                .popover-content > *:not(:last-child) {
                    border-bottom: 1px solid var(--neutrals-grey-06);
                }

                .popover-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 8px;
                    padding: 20px 16px;
                    font-size: 20px;
                    font-family: MaerskText;
                }

                .activity-log-icon {
                    position: relative;
                }

                .red-dot {
                    width: 12px;
                    height: 12px;
                    background-color: #c83819;
                    border-radius: 50%;
                    position: absolute;
                    top: -1px;
                    left: -1px;
                }
            `,
        ]
    };

    handleNotificationsUpdate() {
        this.notifications = VesselNotificationsModel?.notifications || []
        this.activityLogItems = createActivityLogItems(this.notifications)
        this.requestUpdate()
    }

    filteredItems(query: string) {
        if (!query || query.trim() === '') {
            return this.activityLogItems
        }

        const normalizedQuery = query.toLowerCase().trim()

        return this.activityLogItems.filter((notification) => {
            const titleMatch = notification.title
                ? notification.title.toLowerCase().includes(normalizedQuery)
                : false

            const descriptionMatch = notification.description
                ? notification.description
                      .toLowerCase()
                      .includes(normalizedQuery)
                : false

            return titleMatch || descriptionMatch
        })
    }

    handleSearchInput(e: Event) {
        const input = e.target as HTMLInputElement
        this.searchQuery = input.value
    }

    render() {
        const filteredActivityLogItems = this.filteredItems(this.searchQuery)

        return html`
            <mc-popover width="354px" position="bottom-center">
                <span slot="trigger" class="popover-trigger">
                    <div class="activity-log-icon">
                        <mc-icon icon="file"></mc-icon>
                        ${this.activityLogItems.length
                            ? html`<span class="red-dot"></span>`
                            : null}
                    </div>
                    Activity log
                </span>
                <div class="popover-content">
                    <div class="popover-header">
                        <span>Activity log</span>
                        <!-- TO DO when other activity log items ready -->
                        <mc-switch label="Needs action" @click=""> </mc-switch>
                    </div>
                    <div class="search-container">
                        <mc-input
                            placeholder="Search notifications"
                            icon="magnifying-glass"
                            hiddenlabel
                            .value=${this.searchQuery}
                            @input=${this.handleSearchInput}
                        ></mc-input>
                    </div>
                    <div class="notifications">
                        ${filteredActivityLogItems.length > 0
                            ? filteredActivityLogItems.map(
                                  (notification) =>
                                      html`<notification-item
                                          .notification=${notification}
                                      ></notification-item>`,
                              )
                            : html`<div class="empty-state">
                                  ${this.searchQuery
                                      ? 'No notifications match your search'
                                      : 'No notifications yet'}
                              </div>`}
                    </div>
                </div>
            </mc-popover>
        `
    }
}
